import { Button, Segmented, Skeleton } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EntityIcon } from "../../../GlobalMap/map/EntityIcon"
import { computeFit } from "../../../GlobalMap/map/fit"
import type { ProjectOrLeadListRow } from "../../../GlobalMap/redux/global-map.types"
import { homeCardStyle, homeColor, homeText } from "../../home-theme.ts"
import { HomeCard, type HomeCardEdit } from "../HomeCard.tsx"
import { ChevronRightIcon } from "../home-icons.tsx"
import { useHomeLocations, type LocationScope } from "./useHomeLocations"
import { HomeLocationsMap } from "./HomeLocationsMap"

/**
 * §5's health badge.
 *
 * Kept deliberately separate from the marker colour. A project's marker is
 * green because it IS a project — never because it is healthy — so health is
 * carried in words beside the row, which is also what makes it readable
 * without colour vision.
 */
const HealthBadge = ({ label }: { label: string }) => {
  const normalised = label.toLowerCase()
  const tone = normalised.includes("risk")
    ? { fg: homeColor.atRiskInk, bg: homeColor.warningBg }
    : normalised.includes("delay") || normalised.includes("overdue")
      ? { fg: homeColor.delayedInk, bg: homeColor.criticalBg }
      : { fg: homeColor.onTrackInk, bg: homeColor.successBg }
  return (
    <span
      style={{
        ...homeText.chip,
        color: tone.fg,
        background: tone.bg,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap"
      }}
    >
      {label}
    </span>
  )
}

/** City only — the list has no room for a full street address. */
const cityOf = (row: ProjectOrLeadListRow): string | null => {
  const address = row.location?.address
  if (!address) return null
  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  // "123 Main St, Los Angeles, CA 90012" → "Los Angeles, CA"
  if (parts.length >= 3)
    return `${parts[parts.length - 3]}, ${parts[parts.length - 2]}`
  return parts[parts.length - 1] ?? null
}

const LocationRow = ({
  row,
  selected,
  onSelect
}: {
  row: ProjectOrLeadListRow
  selected: boolean
  onSelect: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isProject = row.entity_type === "project"
  const city = cityOf(row)
  const person = isProject
    ? row.people?.project_manager
    : (row.people?.customer ?? row.people?.owner ?? row.people?.salesperson)
  const open =
    row.actions?.open ??
    (isProject ? `/projects/${row.id}` : `/leads/${row.id}`)

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onSelect()
        }
      }}
      className="home-locations__row"
      data-selected={selected ? "true" : undefined}
    >
      <EntityIcon entityType={row.entity_type} className="size-9" />
      <div
        className="min-w-0"
        style={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <span
          style={{
            ...homeText.chip,
            color: homeColor.inkMuted,
            letterSpacing: "0.04em"
          }}
        >
          {isProject
            ? t("home.locations.project", "PROJECT")
            : t("home.locations.lead", "LEAD")}
        </span>
        <span
          style={{ ...homeText.itemTitle, color: homeColor.ink }}
          className="truncate"
        >
          {row.title ?? row.reference}
        </span>
        <span
          style={{ ...homeText.meta, color: homeColor.inkMuted }}
          className="truncate"
        >
          {[city, person].filter(Boolean).join(" · ") ||
            t("home.locations.noLocation", "Location unavailable")}
        </span>
      </div>
      <div
        style={{
          marginInlineStart: "auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0
        }}
      >
        {/* A lead never gets a progress bar (§5); its stage is the status. */}
        {row.status_label ? <HealthBadge label={row.status_label} /> : null}
        <Button
          type="text"
          size="small"
          aria-label={t("home.locations.open", "Open {{name}}", {
            name: row.title ?? row.reference
          })}
          onClick={(event) => {
            event.stopPropagation()
            navigate(open)
          }}
          icon={<ChevronRightIcon width={16} height={16} />}
        />
      </div>
    </div>
  )
}

/**
 * Locations as ONE composite card: the scope chips, the map and the list
 * share a header and move, resize and remove together in Edit layout. They
 * are one selection — a scope chip narrows the map and the list at once — so
 * splitting them into two cards would let a layout put them apart.
 */
export const HomeLocationsSection = ({
  edit,
  title: customTitle
}: {
  edit?: HomeCardEdit | null
  /** A user's own name for the widget. */
  title?: string
} = {}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [scope, setScope] = useState<LocationScope>("all")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { features, response, rows, counts, unmapped, state, retry } =
    useHomeLocations(scope)

  // `computeFit` reads a full `/features` response. A server that answers a
  // partial or malformed body must not take Home's whole page down with it,
  // so the map simply gets no fit instead.
  const fitTo = useMemo(
    () => (Array.isArray(response?.features) ? computeFit(response) : null),
    [response]
  )

  const title = customTitle ?? t("home.locations.title", "Locations")
  const pinIcon = <EntityIcon entityType="lead" className="size-4" />
  // The widget's one way out, scoped to the filter on screen: Locations'
  // list of the same records.
  const openLocations = (
    <Button
      type="link"
      size="small"
      style={{ padding: 0 }}
      data-testid="home-open-locations"
      onClick={() =>
        navigate(
          `/map?tab=list&layers=${scope === "all" ? "project,lead" : scope}`
        )
      }
    >
      {t("home.locations.openLocations", "Open Locations")}
    </Button>
  )

  if (state === "error") {
    return (
      <HomeCard
        testId="home-locations-card"
        edit={edit}
        icon={pinIcon}
        title={title}
        actions={openLocations}
      >
        <span style={{ ...homeText.body, color: homeColor.inkMuted }}>
          {t("home.locations.error", "Locations could not be loaded.")}
        </span>
        <Button type="link" onClick={retry}>
          {t("common.retry", "Retry")}
        </Button>
      </HomeCard>
    )
  }

  return (
    <HomeCard
      testId="home-locations-card"
      edit={edit}
      icon={pinIcon}
      title={title}
      actions={openLocations}
    >
      <div className="home-locations-card">
        {/* One shared toolbar: the selection drives the map AND the list, and
          the counts are the server's own scoped totals, not a count of the
          rows that happen to be on this page. */}
        {/* The chips carry the SAME `EntityIcon` the markers and the rows use,
          so the filter, the legend and the pins read as one vocabulary. The
          counts are the server's scoped totals, never a count of this page. */}
        <Segmented
          value={scope}
          onChange={(value) => {
            setScope(value as LocationScope)
            setSelectedId(null)
          }}
          options={[
            {
              value: "all",
              label: (
                <span className="home-locations__chip">
                  {t("home.locations.all", "All")} <b>{counts.all}</b>
                </span>
              )
            },
            {
              value: "project",
              label: (
                <span className="home-locations__chip">
                  <EntityIcon entityType="project" className="size-5" />
                  {t("home.locations.projects", "Projects")}{" "}
                  <b>{counts.project}</b>
                </span>
              )
            },
            {
              value: "lead",
              label: (
                <span className="home-locations__chip">
                  <EntityIcon entityType="lead" className="size-5" />
                  {t("home.locations.leads", "Leads")} <b>{counts.lead}</b>
                </span>
              )
            }
          ]}
        />

        <div className="home-locations">
          <div
            className="home-locations__map"
            style={{
              ...homeCardStyle,
              overflow: "hidden",
              position: "relative"
            }}
          >
            {/* Records exist but none could be placed: say so over the map
              rather than leave a blank panel that reads as broken. */}
            {state === "ready" && features.length === 0 && rows.length > 0 ? (
              <div className="home-locations__nomap" role="status">
                {t(
                  "home.locations.noneMapped",
                  "None of these records has a usable address yet, so nothing can be placed on the map."
                )}
              </div>
            ) : null}
            <HomeLocationsMap
              features={features}
              response={response}
              fitTo={fitTo}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </div>

          <div
            className="home-locations__panel"
            style={{
              ...homeCardStyle,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {state === "loading" ? (
              <div style={{ padding: 16 }}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 20 }}>
                <span style={{ ...homeText.body, color: homeColor.inkMuted }}>
                  {t(
                    "home.locations.empty",
                    "No leads or projects to show yet."
                  )}
                </span>
              </div>
            ) : (
              <div className="home-locations__list">
                {rows.map((row) => (
                  <LocationRow
                    key={`${row.entity_type}:${row.id}`}
                    row={row}
                    selected={selectedId === row.id}
                    onSelect={() => setSelectedId(row.id)}
                  />
                ))}
              </div>
            )}
            {/* The list and the markers can legitimately differ, so the gap is
              named rather than left for the reader to notice. */}
            {unmapped > 0 ? (
              <div
                style={{
                  padding: "8px 14px",
                  borderTop: `1px solid ${homeColor.border}`,
                  ...homeText.meta,
                  color: homeColor.inkMuted
                }}
              >
                {t(
                  "home.locations.unmapped",
                  "{{count}} record(s) have no usable address and are not shown on the map.",
                  { count: unmapped }
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </HomeCard>
  )
}
